/* next-action.js (v1_750): the one next action.
 *
 * nextAction(caseState) is a PURE function. It returns the single thing the
 * officer should do now, as { key, label, reason, kind, enabled, target, count }.
 *   kind  'perform' the click runs a platform action (target names the action)
 *         'goto'    the click scrolls to and highlights a panel or row (target)
 *         'wait'    disabled; reason says what is being waited on
 * Precedence is fixed and documented below. FIRST MATCHING ROW WINS.
 *
 * The stage bar button, the stage reason line and the Help assistant's answer to
 * "what do I do next" all read this one function. It reads a normalised caseState
 * (no raw stores): the counts and booleans the case already carries, built by
 * buildNextActionState() from the reconciled case state and the session facts.
 *
 * Standing rules honoured here:
 *  - Never two primary actions, never zero. Every state resolves to one row (21
 *    is the terminal catch-all; 20, Case complete, needs a recorded decision).
 *  - Labels are verb phrases, no jargon, and carry NO hyphens or em dashes (the
 *    text reaches the officer). Counts are live.
 *  - No silent no-op: a control that cannot act is a 'wait' row with a reason,
 *    never a live-looking button that does nothing.
 */

// A count word helper: "1 person" / "3 people", "1 item" / "2 items".
function plural(n, one, many) {
  return (n === 1) ? one : (many || (one + 's'));
}

// v1_966: on a draft row, the risk factor notes come second, after what is to
// be requested, so the headline says first why the case is in Gather.
function flagsSecond(s) {
  const k = s.unackedFlagCount || 0;
  return k ? ` Then ${k} risk ${plural(k, 'factor')} ${plural(k, 'needs', 'need')} a recorded note from you before sign off.` : '';
}

/* ── The 23 rows, in fixed precedence order ────────────────────────────────
 * Rows 7 and 13 are the Reprocess vs Quick Update rule made mechanical: client
 * gave new information => Reprocess; officer changed their own assessment =>
 * Quick Update. Row 7 sits ABOVE row 13 deliberately: client information always
 * wins, because Quick Update does not read the conversation. */
export function nextAction(cs) {
  const s = cs || {};

  // 1. Analysis still assembling the case (provisional).
  if (s.analysisRunning) {
    return {
      key: 'analysis-running',
      label: 'Analysis in progress, please wait',
      reason: 'The case is still being assembled. This assessment is provisional until it completes.',
      kind: 'wait', enabled: false, target: null,
    };
  }

  // 1a. The last analysis run stopped (v1_789): a failed step, or a run the
  //     platform ended at its time limit. The case shows the board from before
  //     that run, so a client's submission may be missing from it. Walk run 18
  //     opened on "Draft client request" beside the stopped engine banner.
  // v1_861: the client submitted and no analysis started within five minutes.
  if (s.analysisStopped && s.analysisNeverStarted) {
    return {
      key: 'reprocess-not-started',
      label: 'Reprocess, the analysis the submission asked for did not start',
      reason: 'The client submitted, but no analysis of the submission started within five minutes, so this case does not reflect it yet. Reprocess to run it.',
      kind: 'perform', enabled: true, target: 'reprocess',
    };
  }
  if (s.analysisStopped) {
    return {
      key: 'reprocess-stopped',
      label: 'Reprocess, the last analysis stopped',
      reason: 'The last analysis run stopped before it finished, so this case may not reflect everything the client has submitted. Reprocess before acting on it.',
      kind: 'perform', enabled: true, target: 'reprocess',
    };
  }

  // 2. Proportionality review did not finish on the last run (Not moderated).
  if (s.proportionalityTruncated) {
    return {
      key: 'reprocess-proportionality',
      label: 'Reprocess to finish the review',
      reason: 'The proportionality review did not complete on the last run, so the request set is shown as generated. Reprocess before deciding what to send.',
      kind: 'perform', enabled: true, target: 'reprocess',
    };
  }

  // 2a. The board did not finish on the last run (v1_783): the conversation
  //     merge was cut off at the token limit twice, or failed. The board is
  //     provisional (documents only), so nothing downstream should be decided on
  //     it until a Reprocess finishes it.
  if (s.boardProvisional) {
    return {
      key: 'reprocess-board',
      label: 'Reprocess to finish the board',
      // Cut off fix (a), 11/10/2026: an interview report cut off twice is saved
      // provisional too; its reason says what may be missing.
      reason: s.boardIncompleteStep === 'interview report'
        ? 'The interview report was cut off before it finished, so items after the cut may be missing. Reprocess before relying on it.'
        : 'The board did not finish on the last run, so it shows the documents without the client\u2019s conversation. Reprocess before relying on it.',
      kind: 'perform', enabled: true, target: 'reprocess',
    };
  }

  // 2b. No tier is in force (model tier pending, 29/10/2026): the risk
  //     assessment did not complete (the engine failed with no earlier
  //     assessment, or the re tier after a submission or a regenerate failed).
  //     The model's proposal is never the tier, so the case shows Assessment
  //     pending and sign off is refused until a Reprocess completes it.
  if (s.assessmentPending) {
    return {
      key: 'assessment-pending',
      label: 'Reprocess to complete the assessment',
      reason: 'Assessment pending. The risk assessment has not completed, so no risk tier is in force and sign off is not available.' +
        (s.assessmentPendingReason ? ' Recorded reason: ' + String(s.assessmentPendingReason).replace(/\.$/, '') + '.' : '') +
        ' Reprocess to complete it.',
      kind: 'perform', enabled: true, target: 'reprocess',
    };
  }

  // 3. Undecided proportionality flags.
  if (s.undecidedPropCount > 0) {
    const n = s.undecidedPropCount;
    return {
      key: 'decide-proportionality',
      label: `Decide ${n} flagged ${plural(n, 'request')}`,
      reason: `${n} proportionality ${plural(n, 'flag')} ${plural(n, 'is', 'are')} undecided. Choose Withdraw or Keep on each before the client request can be drafted.`,
      kind: 'goto', enabled: true, target: 'panel-waiting-v2', count: n,
    };
  }

  // 4. Gather, no request sent yet, open client facing items exist.
  //    v1_833: a question the firm asks every client, still unanswered, is one
  //    of them (Draft Client Request adds it). Never a sign off blocker.
  const fq = s.unansweredFirmQuestionCount || 0;
  const toAsk = (s.openClientItemCount || 0) + fq;
  if (s.stage === 0 && !s.requestSent && toAsk > 0) {
    return {
      key: 'draft-request',
      label: 'Draft client request',
      reason: `${toAsk} ${plural(toAsk, 'item')} to request from the client${fq ? `, including ${fq} ${plural(fq, 'question')} the firm asks every client` : ''}. Draft the request to send ${plural(toAsk, 'it', 'them')} in one message.` + flagsSecond(s),
      kind: 'perform', enabled: true, target: 'draft', count: toAsk,
    };
  }

  // 5a. The verification record has not loaded for this case yet (v1_775). Until
  //     it has, every party would read as unsent and row 5 would offer links
  //     already sent (walk run 14, read in the moment after the case opened).
  if (s.identityLoading && (s.identityNotSentCount > 0 || s.identitySentIncompleteCount > 0)) {
    return {
      key: 'identity-loading',
      label: 'Checking identity verification status',
      reason: 'The identity verification record for this case is still loading. The next step shows once it has.',
      kind: 'wait', enabled: false, target: null,
    };
  }

  // 5a. v1_860: the verification record could not be read. The label cleared
  //     into "Send identity links" as if nobody had one (every party reads as
  //     unsent on an empty record), or, where the read never returned, held
  //     "Checking" with no way on. It now says the read failed and why.
  if (s.identityLoadError) {
    return {
      key: 'identity-read-failed',
      label: 'Identity verification record could not be read',
      reason: `${String(s.identityLoadError).replace(/[.\s]+$/, '')}. Use Read again on the identity panel, or reopen the case; until it reads, who has an identity link and who is verified is not known here.`,
      kind: 'wait', enabled: false, target: null,
    };
  }

  // 5a. A party to verify has no email on file (v1_799). The send cannot reach
  //     them, and row 5 offered it again and again: the send skipped them and
  //     said so only in an alert (confirmation walk, 24/09/26). The officer adds
  //     the email on the identity row first.
  if (s.identityNoEmailCount > 0) {
    const n = s.identityNoEmailCount;
    return {
      key: 'add-party-emails',
      label: `Add an email for ${n} ${plural(n, 'person', 'people')}`,
      reason: `${n} ${plural(n, 'person', 'people')} to verify ${plural(n, 'has', 'have')} no email on file, so no identity link can reach them. Add the email on the identity row; the link can then be sent.`,
      kind: 'goto', enabled: true, target: 'panel-sumsub', count: n,
    };
  }

  // 5. Confirmed UBOs or directors with no identity link sent.
  if (s.identityNotSentCount > 0) {
    const n = s.identityNotSentCount;
    return {
      key: 'send-identity-links',
      label: `Send identity links to ${n} ${plural(n, 'person', 'people')}`,
      reason: `${n} confirmed ${plural(n, 'person', 'people')} ${plural(n, 'has', 'have')} no identity link yet. Links are sent by you, not automatically.`,
      kind: 'perform', enabled: true, target: 'auto-actions', count: n,
    };
  }

  // 6. Client has responded since the last Check responses.
  if (s.clientRespondedSinceCheck) {
    return {
      key: 'check-responses',
      label: 'Check responses',
      reason: 'The client has responded since you last checked. Check responses to see what is answered, partial or still outstanding.',
      kind: 'perform', enabled: true, target: 'check-responses',
    };
  }

  // 7. Client gave NEW information since the last analysis (answers, documents,
  //    delegate uploads). Above row 13: client information always wins.
  if (s.clientNewInfoSinceAnalysis) {
    return {
      key: 'reprocess-client-info',
      label: "Reprocess with the client's new information",
      reason: 'The client has given information the analysis has not taken in yet. Reprocess so the file is rebuilt around it.',
      kind: 'perform', enabled: true, target: 'reprocess',
    };
  }

  // 8. Items in "Client responded, awaiting your review".
  if (s.awaitingReviewCount > 0) {
    const n = s.awaitingReviewCount;
    return {
      key: 'review-answers',
      label: `Review ${n} client ${plural(n, 'answer')}`,
      reason: `${n} client ${plural(n, 'answer')} ${plural(n, 'is', 'are')} waiting for your review.`,
      kind: 'goto', enabled: true, target: 'panel-waiting-v2', count: n,
    };
  }

  // 9. Request sent, items still waiting on the client, nothing new in.
  if (s.requestSent && s.openClientItemCount > 0) {
    const n = s.openClientItemCount;
    return {
      key: 'waiting-on-client',
      label: `Waiting on the client for ${n} ${plural(n, 'item')}`,
      reason: (s.reminderReason || `${n} ${plural(n, 'item')} ${plural(n, 'is', 'are')} still with the client.`)
        + ' You can escalate from the request panel.',
      kind: 'wait', enabled: false, target: null, count: n,
    };
  }

  // 9a. v1_966: Gather because an item has not yet been requested, after an
  //     earlier round closed (no round open, nothing with the client). Row 4
  //     covers only the first request, so this fell to row 10 and the headline
  //     read "1 item to request from the client" over a reason about risk factor
  //     notes. The item to request now comes first, the notes second.
  if (s.stage === 0 && s.requestSent && !s.roundOpen && (s.itemsToRequestCount || 0) > 0) {
    const n = s.itemsToRequestCount;
    return {
      key: 'draft-request',
      label: 'Draft client request',
      reason: `${n} ${plural(n, 'item')} to request from the client ${plural(n, 'has', 'have')} not been requested yet. Draft the request to send ${plural(n, 'it', 'them')} in one message.` + flagsSecond(s),
      kind: 'perform', enabled: true, target: 'draft', count: n,
    };
  }

  // 10. Blocking risk factors not acknowledged.
  if (s.unackedFlagCount > 0) {
    const n = s.unackedFlagCount;
    return {
      key: 'acknowledge-flags',
      label: `Acknowledge ${n} risk ${plural(n, 'factor')}`,
      reason: `${n} risk ${plural(n, 'factor')} ${plural(n, 'needs', 'need')} a recorded note from you before sign off.`,
      kind: 'goto', enabled: true, target: 'panel-rf-v2', count: n,
    };
  }

  // 10a. Screening matches (v1_816). A confirmed match escalates: the case
  //      cannot be approved while it stands. An individual potential match not
  //      yet resolved leads to the Sanctions and PEP panel, where the officer
  //      reads the match detail and resolves it; it read only "Resolve 1
  //      blocking check" on the decision board, which has no control for it.
  if (s.confirmedScreeningCount > 0) {
    const n = s.confirmedScreeningCount;
    return {
      key: 'screening-confirmed',
      label: `Confirmed screening ${plural(n, 'match', 'matches')}: approval blocked`,
      reason: `${n} screening ${plural(n, 'match', 'matches')} ${plural(n, 'is', 'are')} confirmed. The case cannot be approved while ${plural(n, 'it stands', 'they stand')}: decline, or refer under your escalation procedure.`,
      kind: 'goto', enabled: true, target: 'panel-sanctions', count: n,
    };
  }
  if (s.openScreeningMatchCount > 0) {
    const n = s.openScreeningMatchCount;
    return {
      key: 'resolve-screening',
      label: `Resolve ${n} screening ${plural(n, 'match', 'matches')}`,
      reason: `${n} ${plural(n, 'person', 'people')} ${plural(n, 'has', 'have')} a potential screening match. Read the match detail and record "Not the same person" with a reason, or "Confirmed match".`,
      kind: 'goto', enabled: true, target: 'panel-sanctions', count: n,
    };
  }

  // 11. Blocking decision board checks unresolved.
  if (s.blockingCheckCount > 0) {
    const n = s.blockingCheckCount;
    return {
      key: 'resolve-checks',
      label: `Resolve ${n} blocking ${plural(n, 'check')}`,
      reason: `${n} blocking ${plural(n, 'check')} on the decision board ${plural(n, 'is', 'are')} unresolved. Each needs an answer or a recorded reason.`,
      kind: 'goto', enabled: true, target: 'panel-decision-v2', count: n,
    };
  }

  // 11a. Open red lines in the AI review (v1_781). They block approval (the case
  //      state lists them as a blocker), yet no row led to them: walk run 16
  //      ended on "Not ready to decide: 3 open red lines in the AI review". Each
  //      is resolved with a recorded reason in the review panel; Quick Update
  //      (row 13) then folds the resolutions into the review.
  if (s.openRedLineCount > 0) {
    const n = s.openRedLineCount;
    return {
      key: 'resolve-red-lines',
      label: `Resolve ${n} red ${plural(n, 'line')} in the AI review`,
      reason: `${n} red ${plural(n, 'line')} in the AI review ${plural(n, 'is', 'are')} open. Each needs your resolution with a recorded reason before approval.`,
      kind: 'goto', enabled: true, target: 'panel-ai-review', count: n,
    };
  }

  // 11b. Documents not read (upload fix batch, fault g, 15/10/2026). The board does
  //      not use their contents. Closed by a reading that succeeds, or by the
  //      officer marking the document Accepted by MLRO or Superseded.
  if (s.notReadCount > 0 && !s.signedOff) {
    const n = s.notReadCount;
    return {
      key: 'documents-not-read',
      label: `${n} ${plural(n, 'document')} not read`,
      reason: `${n} ${plural(n, 'document')} on the case could not be read, so the board does not use ${plural(n, 'its', 'their')} contents. On the Documents panel use Read again where it is offered, ask the client for a clearer copy, or assess ${plural(n, 'it', 'them')} yourself and mark ${plural(n, 'it', 'them')} Accepted by MLRO.`,
      kind: 'goto', enabled: true, target: 'panel-documents', count: n,
    };
  }

  // 12. Identity verification sent but not complete.
  if (s.identitySentIncompleteCount > 0) {
    const n = s.identitySentIncompleteCount;
    return {
      key: 'waiting-on-identity',
      label: `Waiting on identity verification for ${n} ${plural(n, 'person', 'people')}`,
      reason: `${n} ${plural(n, 'person', 'people')} ${plural(n, 'has', 'have')} an identity link out and not yet complete.`,
      kind: 'wait', enabled: false, target: null, count: n,
    };
  }

  // 13. Officer changed the board (resolutions, acknowledgements, verifications,
  //     tier or entity type override) since the last analysis refresh.
  if (s.officerChangedSinceRefresh) {
    return {
      key: 'quick-update',
      label: 'Quick Update to refresh the analysis',
      reason: 'You have changed the board since the analysis last ran. Quick Update folds your changes into the analysis. It does not read the client conversation.',
      kind: 'perform', enabled: true, target: 'quick-update',
    };
  }

  // 14. Decide, high risk, no Adversarial Review run (recommended, not mandatory).
  //     Guarded on !signedOff so a decided high risk case never reopens it.
  if (s.stage === 2 && s.highRisk && !s.adversarialRun && !s.signedOff) {
    return {
      key: 'adversarial-review',
      label: 'Run Adversarial Review',
      reason: 'This is a high risk case and no Adversarial Review has been run. Recommended before you decide. You can skip to the rationale and the skip is recorded.',
      kind: 'perform', enabled: true, target: 'adversarial-review',
    };
  }

  // 15. Decide, no rationale drafted.
  if (s.stage === 2 && !s.rationalePresent) {
    return {
      key: 'draft-rationale',
      label: 'Draft rationale',
      reason: 'Everything on the board is answered. Draft the rationale from the file; the recorded words are always yours.',
      kind: 'perform', enabled: true, target: 'draft-rationale',
    };
  }

  // 16. Decide, rationale present, not signed off.
  if (s.stage === 2 && s.rationalePresent && !s.signedOff) {
    return {
      key: 'sign-off',
      label: 'Sign off',
      reason: 'The rationale is drafted. Open sign off to record your decision.',
      kind: 'perform', enabled: true, target: 'sign-off',
    };
  }

  // 17. Signed off, second sign off pending.
  if (s.secondSignoffPending) {
    return {
      key: 'waiting-second-signoff',
      label: 'Waiting on second sign off',
      reason: 'Your decision is recorded and the file is with the second signatory.',
      kind: 'wait', enabled: false, target: null,
    };
  }

  // 17b (v1_905). The second signatory returned the file for further work. It is
  //     not approved: the MLRO addresses the note and sends it again.
  if (s.secondSignoffReturned) {
    return {
      key: 'second-signoff-returned',
      label: 'Send for second sign off again',
      reason: 'The second signatory returned the file for further work. Read their note in the case notes, address it, then send the file for second sign off again.',
      kind: 'perform', enabled: true, target: 'second-signoff',
    };
  }

  // 18. Decided, audit record not exported.
  if (s.decided && !s.auditExported) {
    return {
      key: 'export-audit',
      label: 'Export Audit Record',
      reason: 'The decision is recorded. Export the audit record and file the PDF.',
      kind: 'perform', enabled: true, target: 'audit-record',
    };
  }

  // 19. Approved, welcome email not sent. The welcome button exists only once the
  //     client has accepted the terms (v1_787): walk run 17 offered "Send welcome
  //     email" three times on a case with no terms sent, and each click found no
  //     control. With the terms status known, the row says what comes first.
  // v1_814: 'outside' (client terms agreed outside the platform, per the firm's
  // signed policy questionnaire) skips the terms step like 'accepted'.
  if (s.approved && !s.welcomeEmailSent && s.termsStatus && s.termsStatus !== 'accepted' && s.termsStatus !== 'outside') {
    if (s.termsStatus === 'pending') {
      return {
        key: 'waiting-terms',
        label: 'Waiting on the client to accept the terms',
        reason: 'The client terms are out for acceptance. The welcome email follows once the client accepts them.',
        kind: 'wait', enabled: false, target: null,
      };
    }
    // v1_792: the firm has no terms to send (no documents, no fee template). The
    // panel shows no send control and the welcome email has nowhere to come
    // from; the row said "Send the client terms" and led nowhere (walk run 18).
    if (s.termsStatus === 'unconfigured') {
      return {
        key: 'set-up-terms',
        label: 'Set up the client terms',
        reason: 'The relationship is approved, but this firm has no client terms documents or fee template, so there is nothing to send for acceptance and the welcome email cannot follow. An MLRO sets them up once, in Settings under Client terms and fees.',
        kind: 'perform', enabled: true, target: 'terms-settings',
      };
    }
    if (s.termsStatus === 'none') {
      return {
        key: 'send-terms',
        label: 'Send the client terms',
        reason: 'The relationship is approved. Send the client terms for acceptance; the welcome email follows once they are accepted.',
        kind: 'goto', enabled: true, target: 'panel-terms',
      };
    }
    return {
      key: 'terms-loading',
      label: 'Checking the client terms status',
      reason: 'The client terms record for this case is still loading. The next step shows once it has.',
      kind: 'wait', enabled: false, target: null,
    };
  }
  if (s.approved && !s.welcomeEmailSent) {
    return {
      key: 'send-welcome',
      label: 'Send welcome email',
      reason: 'The relationship is approved. The welcome email is a deliberate click, sent once terms are accepted.',
      kind: 'perform', enabled: true, target: 'welcome',
    };
  }

  // 20. Everything done: reached only once a decision is recorded (v1_772).
  if (s.decided || s.signedOff) {
    return {
      key: 'complete',
      label: 'Case complete',
      reason: 'The decision is recorded and nothing further is outstanding on this case.',
      kind: 'wait', enabled: false, target: null,
    };
  }

  // 21. No decision yet and no other row fires (v1_772). This was the "Case
  //     complete, nothing is outstanding" row, reached on an undecided case with
  //     two directors unverified (walk run 12). It now says what is missing: the
  //     board's outstanding items, or, when none is recorded, that the board has
  //     not reached Decide, rather than claiming the case is complete.
  const missing = (s.outstanding || []).filter(Boolean);
  const n = missing.length;
  return {
    key: 'not-ready',
    label: n === 0 ? 'Not ready to decide: check the decision board'
      : n === 1 ? `Not ready to decide: ${missing[0]}`
      : `Not ready to decide: ${missing[0]} and ${n - 1} more`,
    reason: n === 0
      ? 'No decision is recorded and the case has not reached Decide, but no outstanding item is recorded either. Open the decision board to see what holds it.'
      : `No decision is recorded. Still outstanding: ${missing.join('; ')}.`,
    kind: 'goto', enabled: true, target: 'panel-decision-v2', count: n,
  };
}

/* ── Builder ────────────────────────────────────────────────────────────────
 * buildNextActionState() turns the reconciled case state and the raw session
 * facts into the normalised caseState nextAction reads. It owns the ONE piece of
 * logic worth unit testing on its own: the row 7 vs row 13 timestamp rule.
 *
 * Row 7  (client new info):  clientInfoAt  > lastAnalysisAt
 *   lastAnalysisAt is the last FULL analysis that folded the conversation and
 *   documents in (report.riskAssessment.computedAt). Quick Update does not move
 *   it, so a Quick Update never clears row 7; a Reprocess does.
 * Row 13 (officer board change):  officerChangeAt > max(lastAnalysisAt, lastQuickUpdateAt)
 *   A Quick Update refreshes the analysis with respect to board changes, so it
 *   must clear row 13; hence Quick Update stamps its own time (lastQuickUpdateAt)
 *   and row 13 anchors on the LATER of the two. Entity type pin and manual
 *   re-tier both re-run the full engine and move lastAnalysisAt, so they clear
 *   through the anchor and need no separate stamp.
 */
function ms(t) {
  if (!t) return 0;
  const n = (t instanceof Date) ? t.getTime() : new Date(t).getTime();
  return Number.isFinite(n) ? n : 0;
}

export function buildNextActionState(input) {
  const x = input || {};

  const lastAnalysisAt = ms(x.lastAnalysisAt);
  const lastQuickUpdateAt = ms(x.lastQuickUpdateAt);
  const clientInfoAt = ms(x.clientInfoAt);
  const officerChangeAt = ms(x.officerChangeAt);

  // Row 7: client information newer than the last full analysis. When the case
  // has never been analysed (no anchor) any client information counts.
  const clientNewInfoSinceAnalysis = !!x.hasClientInfo && (
    lastAnalysisAt === 0 ? clientInfoAt > 0 : clientInfoAt > lastAnalysisAt
  );

  // Row 13: officer board change newer than the LATER of the last analysis and
  // the last Quick Update.
  // v1_819: when the case has an AI review, the anchor is the review's own time
  // (reviewAnchorAt): a re tier or a Reprocess does not refresh the review, so it
  // does not clear the row. Without a review, the engine anchor as before.
  const reviewAnchorAt = ms(x.reviewAnchorAt);
  const refreshAnchor = reviewAnchorAt > 0 ? reviewAnchorAt : Math.max(lastAnalysisAt, lastQuickUpdateAt);
  const officerChangedSinceRefresh = officerChangeAt > 0 && officerChangeAt > refreshAnchor;

  return {
    analysisRunning: !!x.analysisRunning,
    analysisStopped: !!x.analysisStopped,
    analysisNeverStarted: !!x.analysisNeverStarted,
    proportionalityTruncated: !!x.proportionalityTruncated,
    boardProvisional: !!x.boardProvisional,
    boardIncompleteStep: x.boardIncompleteStep || null,
    // Model tier pending (29/10/2026): no tier in force, and the recorded reason.
    assessmentPending: !!x.assessmentPending,
    assessmentPendingReason: x.assessmentPendingReason ? String(x.assessmentPendingReason) : null,
    undecidedPropCount: x.undecidedPropCount || 0,
    openScreeningMatchCount: x.openScreeningMatchCount || 0,
    confirmedScreeningCount: x.confirmedScreeningCount || 0,
    stage: (typeof x.stage === 'number') ? x.stage : 0,
    requestSent: !!x.requestSent,
    openClientItemCount: x.openClientItemCount || 0,
    // v1_966: in Gather with no round open, the items still to request (the
    // count the headline shows), and whether a round is open.
    itemsToRequestCount: x.itemsToRequestCount || 0,
    roundOpen: !!x.roundOpen,
    // v1_833: questions the firm asks every client, not yet answered.
    unansweredFirmQuestionCount: x.unansweredFirmQuestionCount || 0,
    identityNotSentCount: x.identityNotSentCount || 0,
    identityNoEmailCount: x.identityNoEmailCount || 0,
    identityLoading: !!x.identityLoading,
    identityLoadError: x.identityLoadError ? String(x.identityLoadError) : null,
    clientRespondedSinceCheck: !!x.clientRespondedSinceCheck,
    clientNewInfoSinceAnalysis,
    awaitingReviewCount: x.awaitingReviewCount || 0,
    reminderReason: x.reminderReason || null,
    unackedFlagCount: x.unackedFlagCount || 0,
    blockingCheckCount: x.blockingCheckCount || 0,
    openRedLineCount: x.openRedLineCount || 0,
    notReadCount: x.notReadCount || 0,
    identitySentIncompleteCount: x.identitySentIncompleteCount || 0,
    officerChangedSinceRefresh,
    highRisk: !!x.highRisk,
    adversarialRun: !!x.adversarialRun,
    rationalePresent: !!x.rationalePresent,
    signedOff: !!x.signedOff,
    secondSignoffPending: !!x.secondSignoffPending,
    secondSignoffReturned: !!x.secondSignoffReturned,
    decided: !!x.decided,
    auditExported: !!x.auditExported,
    approved: !!x.approved,
    welcomeEmailSent: !!x.welcomeEmailSent,
    // v1_787: 'accepted' | 'pending' | 'none' | 'loading'; absent keeps row 19 as it was.
    termsStatus: x.termsStatus || null,
    // v1_772: what the board still holds open, in the officer's words.
    outstanding: Array.isArray(x.outstanding) ? x.outstanding.filter(t => typeof t === 'string' && t.trim()) : [],
  };
}

// v1_802: ONE RULE FOR RUNNING AND STOPPED, read by the dashboard's Next action
// (row 1a), its two engine banners and the client's progress strip. The
// document engine now runs as a chain of calls (api/df-process.js); between two
// of them the strip carries a 'handoff' line while the next call claims its
// stage. That gap counts as running for up to two minutes; a handoff nobody
// claimed after that is stopped, as is a 'failed' line (a stage that did not
// start says so there) or a strip with no activity for fifteen minutes while not
// complete (the platform ended a call; one call cannot run longer than 800 s).
// v1_805: a pending stage waits for its driver: the browser following the run
// within seconds, or with no browser the engine sweep cron, which takes a stage
// pending two minutes on its next minute tick. So a pending stage is running for
// up to five minutes (two, plus the tick, plus margin), and stopped after that.
export const HANDOFF_STALL_MS = 5 * 60 * 1000;
export const STEP_STALL_MS = 15 * 60 * 1000;
//
// v1_804: A POLL THAT STARTED A RUN FOLLOWS THAT RUN. Pass the runId the start
// answered with (opts.runId) and the time the start was asked for (opts.since):
// only lines carrying that runId count, so an earlier run's complete line (still
// on the strip, or written back by that run's late sweep) never reads as this
// run finishing. Until a line of this run appears the run is starting (running),
// and stopped once nothing of it has appeared for fifteen minutes. Without a
// runId the rule reads the whole strip, as before.
export function analysisRunState(progress, nowMs = Date.now(), opts = {}) {
  const all = Array.isArray(progress) ? progress.filter(Boolean) : [];
  const runId = opts && opts.runId;
  if (runId) {
    const ap = all.filter(e => e.runId === runId || (e.meta && e.meta.runId === runId));
    if (!ap.length) {
      const since = Number(opts.since) || 0;
      return since && nowMs - since > STEP_STALL_MS ? { running: false, stopped: true, complete: false, why: 'never-started' } : { running: true, stopped: false, complete: false };
    }
    return analysisRunState(ap, nowMs);
  }
  const ap = all;
  const at = (e) => { const n = e && e.at ? new Date(e.at).getTime() : NaN; return Number.isFinite(n) ? n : 0; };
  if (!ap.length || ap.some(e => e.key === 'complete')) return { running: false, stopped: false, complete: ap.some(e => e.key === 'complete') };
  if (ap.some(e => e.status === 'failed')) return { running: false, stopped: true, complete: false, why: 'failed' };
  const newest = ap.reduce((a, e) => (!a || at(e) > at(a)) ? e : a, null);
  const age = nowMs - at(newest);
  if (newest && newest.key === 'handoff' && newest.status === 'active') {
    return age > HANDOFF_STALL_MS ? { running: false, stopped: true, complete: false, why: 'handoff' } : { running: true, stopped: false, complete: false };
  }
  if (age > STEP_STALL_MS) return { running: false, stopped: true, complete: false, why: 'stalled' };
  return { running: true, stopped: false, complete: false };
}

// v1_861: ANALYSIS IN PROGRESS FROM THE MOMENT OF SUBMISSION. The strip is
// written by the rebuild itself, only once it has read the case and the firm
// configuration, so between the client's submission and that first line the
// case read as the last completed run (walk we48c7d7f: "Draft client request"
// for about 80 seconds, and a check run against the board from before the
// submission). The submission stamps collected_data.analysisRequestedAt
// (server time, api/update-session.js); until the strip has a line at or after
// it, the case is analysing. Five minutes with no line reads as stopped (the
// rebuild never started), never as finished.
export const REQUEST_START_MS = HANDOFF_STALL_MS;
export function caseAnalysisState(progress, collected, nowMs = Date.now()) {
  const cd = (collected && typeof collected === 'object') ? collected : {};
  const req = Date.parse(cd.analysisRequestedAt || '') || 0;
  if (req) {
    const all = Array.isArray(progress) ? progress.filter(Boolean) : [];
    const newest = all.reduce((m, e) => Math.max(m, Date.parse((e && e.at) || '') || 0), 0);
    if (newest < req) {
      return nowMs - req > REQUEST_START_MS
        ? { running: false, stopped: true, complete: false, why: 'never-started', requestedAt: cd.analysisRequestedAt }
        : { running: true, stopped: false, complete: false, why: 'requested', requestedAt: cd.analysisRequestedAt };
    }
  }
  return analysisRunState(progress, nowMs);
}

export default nextAction;
